<template>
    <div class="broker-clubs">
        <!-- Loading Overlay -->
        <LoadingOverlay :loading="isLoading" :message="loadingMessage" />

        <!-- Skeleton Loader for initial load -->
        <SkeletonLoader v-if="isInitialLoading" type="default" />

        <!-- Main Content (hidden during initial load) -->
        <div v-show="!isInitialLoading">
            <div class="clubs-header">
                <div class="d-flex justify-space-between align-start">
                    <div>
                        <h1 class="text-h1 font-weight-bold mb-3">{{ $t('broker.clubs.title') }}</h1>
                        <p class="text-body-1 text--darken-2 ma-0">
                            {{ $t('broker.clubs.header.subtitle') }}
                        </p>
                    </div>
                    <v-btn outlined rounded class="create-club-btn" :disabled="isReadOnlyStaff" :class="{ 'btn-disabled': isReadOnlyStaff }" @click="openCreateModal">
                        <v-icon left size="18">fa-regular fa-user</v-icon>
                        {{ $t('broker.clubs.header.createBtn') }}
                    </v-btn>
                </div>
            </div>
            <!-- Clubs List -->
            <div class="clubs-list">
                <div class="d-flex align-center mb-3">
                    <h2 class="text-h2 font-weight-bold mr-6">{{ $t('broker.clubs.myCommunities.title') }}</h2>
                    <v-text-field
                        v-model="searchQuery"
                        :placeholder="$t('broker.common.search')"
                        prepend-inner-icon="fa-regular fa-magnifying-glass"
                        outlined
                        dense
                        hide-details
                        class="search-field"
                        style="max-width: 300px;"
                    ></v-text-field>
                </div>
                <v-row class="mx-n5"> <!-- negative margin to offset the card padding -->
                    <v-col v-for="club in filteredClubs" :key="club.club.id" cols="12" sm="6" md="4" lg="4" class="px-5 pb-6">
                        <!-- consistent padding for gaps -->
                        <v-card
                            class="club-card"
                            elevation="0"
                            :class="{ 'clickable-card': club.club.partnerOwned }"
                            @click="handleClubClick(club)"
                        >
                            <v-card-title class="font-weight-medium d-flex justify-end align-center owner-badge" style="padding-right: 28px; padding-left: 28px;">
                                <span v-if="club.club.partnerOwned == true" style="font-size: 14px; line-height: 2;"
                                    class="badge-text">{{ $t('broker.clubs.myCommunities.owner') }}</span>
                                <span v-else style="font-size: 14px; height: 29.6px;"></span>
                            </v-card-title>
                            <v-card-text>
                                <v-col class="d-flex align-center">
                                    <v-avatar size="78" class="mr-4 avatar-border" style="display: inline-block;">
                                        <v-img style="margin-top: -2px;" :src="club.club.imageUrl" :alt="club.club.name"
                                            contain top></v-img>
                                    </v-avatar>
                                    <h2 style="display: inline;">{{ club.club.name }}</h2>
                                </v-col>
                                <v-row cols="12" justify="space-between">
                                    <span class="d-flex col-md-5 col-lg-6"><v-icon class="mr-2" color="#000"
                                            small>fa-regular fa-calendar-week</v-icon> {{
                                                $t('broker.clubs.myCommunities.createdAt', {
                                                    date:
                                        club.club.createdAt.split('T')[0] }) }}</span>
                                    <span class="d-flex col-md-5 col-lg-6 justify-end align-center"><v-icon class="mr-2" color="#000"
                                            small>fa-regular fa-user</v-icon> {{
                                                $t('broker.clubs.myCommunities.participants', { count: club.participantCount })
                                        }}</span>
                                </v-row>
                            </v-card-text>
                            <v-card-actions>

                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Community Creation Modals -->
        <create-community-modal
            v-if="showCreateModal"
            :is-open="showCreateModal"
            @owner-flow="openWizard"
            @assign-flow="openAssignFlow"
            @close="showCreateModal = false"
        />

        <!-- Assign Owner Modal -->
        <assign-owner-modal
            v-if="showAssignModal"
            :is-open="showAssignModal"
            @close="showAssignModal = false"
            @invitation-sent="onAssignInvitationSent"
        />

        <!-- Assign Invitation Sent Modal -->
        <assign-invite-sent-modal
            v-if="showAssignInviteSentModal"
            :is-open="showAssignInviteSentModal"
            :recipient-name="assignRecipient.name"
            :recipient-email="assignRecipient.email"
            @close="closeAssignInviteSentModal"
        />
    </div>
</template>

<script>
import clubService from '../services/club.service'
import LoadingOverlay from '../components/LoadingOverlay.vue'
import SkeletonLoader from '../components/SkeletonLoader.vue'
import CreateCommunityModal from '../components/CommunityWizard/CreateCommunityModal.vue'
import AssignOwnerModal from '../components/CommunityWizard/AssignOwnerModal.vue'
import AssignInviteSentModal from '../components/CommunityWizard/AssignInviteSentModal.vue'
import ClubRepository from '@/repositories/club.js'

export default {
    name: 'BrokerClubs',
    components: {
        LoadingOverlay,
        SkeletonLoader,
        CreateCommunityModal,
        AssignOwnerModal,
        AssignInviteSentModal
    },
    data() {
        return {
            loading: false,
            isLoading: false,
            isInitialLoading: true,
            loadingMessage: 'Loading communities...',
            clubs: [],
            searchQuery: '',
            showCreateModal: false,
            showAssignModal: false,
            showAssignInviteSentModal: false,
            assignRecipient: {
                name: '',
                email: ''
            }
        }
    },
    computed: {
        filteredClubs() {
            if (!this.searchQuery) return this.clubs;
            const query = this.searchQuery.toLowerCase();
            return this.clubs.filter(club => club.club.name.toLowerCase().includes(query));
        },
        isReadOnlyStaff() {
            return this.$store.getters['broker/isReadOnlyStaff']
        },
    },
    methods: {
        loadPartnerClubs() {
            this.isLoading = true
            this.loadingMessage = 'Loading communities...'
            clubService.getAllPartnerClubs()
                .then(clubs => {
                    this.clubs = clubs
                })
                .catch(error => {
                    console.error('Error loading partner clubs:', error)
                })
                .finally(() => {
                    this.isLoading = false
                    this.isInitialLoading = false
                })
        },

        openCreateModal() {
            if (this.isReadOnlyStaff) return
            this.showCreateModal = true
        },

        openWizard() {
            this.showCreateModal = false
            // Navigate to wizard route with owner flow
            this.$router.push({ path: '/clubs/create', query: { flow: 'owner' } })
        },

        openAssignFlow() {
            this.showCreateModal = false
            // Show the assign owner modal
            this.showAssignModal = true
        },

        onAssignInvitationSent({ name, email }) {
            // Close the assign modal
            this.showAssignModal = false
            // Store recipient info
            this.assignRecipient = { name, email }
            // Show the confirmation modal
            this.showAssignInviteSentModal = true
        },

        closeAssignInviteSentModal() {
            this.showAssignInviteSentModal = false
            // Reset recipient info
            this.assignRecipient = { name: '', email: '' }
            // Reload clubs list to show any updates
            this.loadPartnerClubs()
        },

        async handleClubClick(club) {
            // Only allow navigation for partner-owned clubs
            if (!club.club.partnerOwned) {
                return
            }

            try {
                this.isLoading = true
                this.loadingMessage = 'Loading community...'

                // Fetch club members like ClubModule does
                const response = await ClubRepository.getAllClubMembers(club.club.id)
                const clubMembers = response.data || []

                // Transform club structure to match what ClubIndex/ClubMenu expects
                // Traders app structure: { id: clubMemberId, rol: 'FOUNDER', club: {...} }
                // Broker receives: { club: {...}, participantCount: N }
                const clubMemberData = {
                    id: club.club.id, // Use club id as clubMember id for broker context
                    rol: 'FOUNDER',   // Partner-owned clubs = user is FOUNDER
                    club: club.club
                }

                // Set club member in store (matching ClubModule's clubSelection logic)
                this.$store.commit('setClubMember', clubMemberData)

                // Set club members in store (extract .data from response)
                this.$store.dispatch('setClubMembers', clubMembers)

                // Navigate to club index (same route as ClubModule)
                this.$router.push('/club')
            } catch (error) {
                console.error('Error loading club:', error)
                // You could add a snackbar/toast notification here
            } finally {
                this.isLoading = false
            }
        }
    },
    async mounted() {
        this.isInitialLoading = true
        try {
            await this.loadPartnerClubs()
        } finally {
            // Add a small delay to make the transition smoother
            setTimeout(() => {
                this.isInitialLoading = false
            }, 500)
        }
    }
}
</script>

<style lang="scss" scoped>
.broker-clubs {

    .clubs-header {
        margin-bottom: 60px;

        .text-body-1 {
            line-height: 1.6 !important;
            max-width: 800px;
        }

        .btn-disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .create-club-btn {
            border-radius: 24px !important;
            text-transform: none;
            font-weight: 500;
            font-size: 14px;
            padding: 0 10px !important;
            padding-left: 14px !important;
            margin-top: auto;
            border: 1px solid #005931 !important;
            background-color: transparent !important;
            color: #005931 !important;

            &:hover {
                background-color: rgba(0, 89, 49, 0.04) !important;
            }

            .v-icon {
                margin-right: 8px !important;
                color: #005931 !important;
                font-size: 16px !important;
            }
        }
    }

    .search-field {
        margin-left: auto;
        .v-input__prepend-inner {
            margin-top: 8px;
        }
        .v-text-field__slot input {
            font-size: 14px;
        }
    }
    .clubs-list {

        // Remove default row margins to align with other components
        .v-row {
            margin-top: 0 !important;
        }

        .club-card {
            border-radius: 12px !important;
            transition: all 0.3s ease;
            background-color: #FFFFFB !important;
            height: 100%; // Ensure consistent card heights
            box-shadow: 5px 5px 20px 0px #0000000D !important;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0px 8px 16px 0px rgba(44, 44, 44, 0.15) !important;
            }

            // Add cursor pointer for clickable (owner) cards
            &.clickable-card {
                cursor: pointer;

                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0px 8px 16px 0px rgba(40, 234, 150, 0.25) !important;
                }
            }

            .owner-badge .badge-text {
                border: 1px solid #007a5e !important;
                border-radius: 24px !important;
                padding: 0px 12px;
                color: #007a5e !important;
            }

            .avatar-border {
                border: 3px solid #28ea96 !important;
            }
        }
    }

}
</style>